<template>
	<button
		ref="buttonRef"
		class="_form-button relative -p-large"
		data-hitarea
		:data-loading="loading"
		type="submit"
		@mouseenter="onMouseEnter"
	>
		<span class="underline-wrapper">
			<span>{{ label }}</span>
			<span class="underline-line" data-underline-line />
			<span class="underline-line" data-underline-line />
		</span>
	</button>
</template>

<script>
import { gsap } from 'gsap'

function createMotion({ container, underline }) {
	let loadingTimeline = null

	function killTweens() {
		if (loadingTimeline) {
			loadingTimeline.kill()
			loadingTimeline = null
		}

		gsap.killTweensOf([container, ...underline])
	}

	function hover() {
		if (underline.length < 2) return

		killTweens()

		gsap.fromTo(
			underline[0],
			{ scaleX: 1, transformOrigin: 'right' },
			{ duration: 0.6, ease: 'power3.in', scaleX: 0 },
		)
		gsap.fromTo(
			underline[1],
			{ scaleX: 0, transformOrigin: 'left' },
			{ duration: 0.8, delay: 0.5, ease: 'power3', scaleX: 1 },
		)
	}

	function loading() {
		if (underline.length < 2) return

		killTweens()
		gsap.to(container, { duration: 0.3, ease: 'power2.inOut', opacity: 0.4 })

		const timeline = gsap.timeline({ repeat: -1 })

		timeline.fromTo(
			underline[0],
			{ scaleX: 1, transformOrigin: 'right' },
			{ duration: 0.8, ease: 'power2.inOut', scaleX: 0 },
			0,
		)
		timeline.fromTo(
			underline[1],
			{ scaleX: 0, transformOrigin: 'left' },
			{ duration: 0.8, delay: 0.3, ease: 'power2.inOut', scaleX: 1 },
			0,
		)

		loadingTimeline = timeline
	}

	function killLoading() {
		killTweens()
		gsap.to(container, { duration: 0.2, ease: 'power2.out', opacity: 1 })
	}

	function destroy() {
		killTweens()
	}

	return { hover, loading, killLoading, destroy }
}

function isTouchDevice() {
	if (!import.meta.client) return false
	return window.matchMedia('(hover: none), (pointer: coarse)').matches
}

export default {
	props: {
		label: { type: String, required: true },
		disabled: { type: Boolean, default: false },
		loading: { type: Boolean, default: false },
	},
	data() {
		return {
			motion: null,
		}
	},
	watch: {
		loading(value) {
			if (!this.motion) return

			if (value) {
				this.motion.loading()
			} else {
				this.motion.killLoading()
			}
		},
	},
	beforeUnmount() {
		this.motion?.destroy?.()
	},
	mounted() {
		const underline = [...this.$el.querySelectorAll('[data-underline-line]')]
		this.motion = createMotion({
			container: this.$el,
			underline,
		})
	},
	methods: {
		onMouseEnter() {
			if (!this.loading && !isTouchDevice()) {
				this.motion?.hover()
			}
		},
	},
}
</script>

<style lang="scss">
._form-button {
	&[data-loading='true'] {
		cursor: wait;
	}

	.underline-wrapper {
		position: relative;
		display: inline-block;
	}

	.underline-line {
		position: absolute;
		width: 100%;
		height: 0.066em;
		left: 0;
		bottom: -0.033em;
		background-color: currentColor;
		transform-origin: left;

		&:last-child {
			transform: scaleX(0);
		}
	}
}
</style>
